import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { aplicarAlteracao, PlanoMudou, prepararAlteracao, type Plano } from '../api/alteracoes'
import { nomePlataforma } from '../api/trafegoPago'
import { descreverTipo, formatarValor, nomeNivel, PLATAFORMAS_ALTERAVEIS, type Nivel, type Tipo } from '../utils/alteracoes'

export type PedidoAlteracao = { plataforma: string; nivel: Nivel; objetoId: string; nome: string; tipo: Tipo; percentual: number | null }

const PASSOS = [5, 10, 15, 20]

/**
 * Confirmação de uma alteração na campanha. O antes e o depois são lidos da plataforma na hora
 * (não da última coleta); só depois do clique em "Aplicar no …" algo muda. A IA nunca
 * chega aqui sozinha: quem abre é uma pessoa com campanhas.alterar.
 * Quem abre passa `key` por pedido, para o diálogo nascer limpo a cada um.
 */
export function AplicarAlteracao({
  pedido,
  origem,
  motivo = null,
  onFechar,
  onAplicada,
}: {
  pedido: PedidoAlteracao
  origem: 'sugestao' | 'manual'
  motivo?: string | null
  onFechar: () => void
  onAplicada?: () => void
}) {
  const [percentual, setPercentual] = useState<number | null>(pedido.percentual)
  const [plano, setPlano] = useState<Plano | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [mudou, setMudou] = useState<string | null>(null)
  const [aplicando, setAplicando] = useState(false)
  const [feito, setFeito] = useState(false)

  const chave = JSON.stringify({ ...pedido, percentual })
  useEffect(() => {
    const p = JSON.parse(chave) as PedidoAlteracao
    if (p.tipo === 'orcamento' && p.percentual == null) return
    let cancelado = false
    setPlano(null)
    setErro(null)
    prepararAlteracao(p)
      .then((r) => !cancelado && setPlano(r))
      .catch((e: Error) => !cancelado && setErro(e.message))
    return () => {
      cancelado = true
    }
  }, [chave])

  const orcamento = pedido.tipo === 'orcamento'
  const onde = PLATAFORMAS_ALTERAVEIS[pedido.plataforma] ?? nomePlataforma(pedido.plataforma)
  const sinal = (percentual ?? 0) < 0 ? -1 : 1
  const itens = plano?.itens ?? []
  const pode = !!plano && !plano.bloqueio && itens.length > 0 && !aplicando && !feito

  const aplicar = async () => {
    if (!plano?.itens) return
    setAplicando(true)
    setErro(null)
    setMudou(null)
    try {
      await aplicarAlteracao({ ...pedido, percentual }, plano.itens, origem, motivo)
      setFeito(true)
      onAplicada?.()
    } catch (e) {
      if (e instanceof PlanoMudou) {
        setPlano(e.plano)
        setMudou(e.message)
      } else setErro((e as Error).message)
    } finally {
      setAplicando(false)
    }
  }

  return (
    <Dialog open onClose={aplicando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {descreverTipo(pedido.tipo, orcamento ? percentual : null)}
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>
          {nomeNivel(pedido.nivel, pedido.plataforma)} · {plano?.alvo.nome ?? pedido.nome} · {nomePlataforma(pedido.plataforma)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {orcamento && !feito ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={sinal}
                onChange={(_, v: number | null) => v && setPercentual(Math.abs(percentual ?? 20) * v)}
                aria-label="Direção do ajuste"
              >
                <ToggleButton value={1} sx={{ textTransform: 'none' }}>
                  Aumentar
                </ToggleButton>
                <ToggleButton value={-1} sx={{ textTransform: 'none' }}>
                  Reduzir
                </ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={Math.abs(percentual ?? 20)}
                onChange={(_, v: number | null) => v && setPercentual(v * sinal)}
                aria-label="Tamanho do ajuste"
              >
                {PASSOS.map((p) => (
                  <ToggleButton key={p} value={p} sx={{ px: 1.5 }}>
                    {p}%
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          ) : null}

          {erro ? <Alert severity="error">{erro}</Alert> : null}
          {mudou ? <Alert severity="warning">{mudou}</Alert> : null}
          {!plano && !erro ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
          {plano?.bloqueio ? <Alert severity="info">{plano.bloqueio}</Alert> : null}

          {itens.length ? (
            <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{itens.length > 1 ? nomeNivel('conjunto', pedido.plataforma) : nomeNivel(itens[0].nivel, pedido.plataforma)}</TableCell>
                  <TableCell align="right">Agora no {onde}</TableCell>
                  <TableCell align="right">{feito ? 'Ficou' : 'Vai ficar'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((i) => (
                  <TableRow key={`${i.id_externo}-${i.campo}`}>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{i.nome}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {formatarValor(i, i.antes, plano!.alvo.moeda)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                      {formatarValor(i, i.depois, plano!.alvo.moeda)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}

          {(plano?.avisos ?? []).map((a) => (
            <Alert key={a} severity="info" variant="outlined">
              {a}
            </Alert>
          ))}

          {motivo ? (
            <Typography variant="body2" color="text.secondary">
              <strong>Por quê:</strong> {motivo}
            </Typography>
          ) : null}

          {feito ? (
            <Alert severity="success">
              Aplicado no {onde}. A alteração ficou no <RouterLink to="/alteracoes">registro de alterações</RouterLink>, de onde dá para desfazer.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary">
              A mudança vai direto para o {nomePlataforma(pedido.plataforma)} assim que você confirmar e fica no registro de alterações com o seu nome. Os números do
              painel só mudam na próxima coleta.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={aplicando} sx={{ textTransform: 'none' }}>
          {feito ? 'Fechar' : 'Cancelar'}
        </Button>
        {!feito ? (
          <Button variant="contained" color={pedido.tipo === 'pausar' ? 'error' : 'primary'} disabled={!pode} onClick={aplicar} sx={{ textTransform: 'none' }}>
            {aplicando ? 'Aplicando…' : `Aplicar no ${onde}`}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
